// The v2.0.41 frame: dark sidebar, header with title, workspace badge, date/time and calendar.
import { esc, get, initials, post, toast, orgToday } from "./lib.js";
import { MAIN_REPORTS, reportShown, type MainReportId } from "../../shared/reportsmenu.js";
import { icon } from "./icons.js";
import { fullName, onAction, state } from "./state.js";
import { isQcInspector, type Actor } from "../../shared/permissions.js";
import { MENU, rowOf, type MenuItem } from "../../shared/menu.js";
import { actorLevel, atLeast, financeOpen } from "../../shared/access.js";
import { langButtonHtml, t } from "./i18n.js";
import { profileHtml, profileOpenState, restoreProfile } from "./profile.js";
import { checkOrgLogos } from "./orglogo.js";
import { CRM_GROUP, crmMenu } from "../../shared/crm.js";
import { bothPortals, inCrmPortal, notePortal, rememberPortal } from "./views/crmaccess.js";

/** The older permission page of a menu item (for checks written against pages). */
export const pageFor = (id: string) => (id === "trims" ? "products" : id === "artwork" || id === "cad" ? "techpacks" : id === "timeline" ? "po" : id === "qcready" ? "qc" : id);
/** Can this person open a menu item? (their level on its row; Falcon support: everything) */
export const canOpen = (id: string, actor: Actor | null = state.session.actor ?? null) => atLeast(actorLevel(actor, rowOf(id)), "view");

export type NavItem = MenuItem;
/** The sidebar menu: the shared definition, the same one the permissions screen uses for its rows. */
export const PLM_NAV: NavItem[] = MENU;

/** A QC inspector's whole menu (plus Help): Ready for QC and their own inspections. */
const INSPECTOR_NAV: NavItem[] = [
  { id: "qcready", label: "Ready for QC", href: "/app/qc/ready" },
  { id: "qcmine", label: "My inspections", href: "/app/qc/mine" },
];
export const isInspector = () => isQcInspector(state.session.actor);

const CONSOLE_NAV: NavItem[] = [
  { id: "platformdashboard", label: "Overview", href: "/console" },
  { id: "platformorganisations", label: "Organisations & Billing", href: "/console/organisations" },
  { id: "platformagreements", label: "Agreements", href: "/console/agreements" },
  { id: "platformprices", label: "Website Prices", href: "/console/website-prices" },
  { id: "platformsupport", label: "Support", href: "/console/support" },
  { id: "platformcopy", label: "Copy Setup", href: "/console/copy-setup" },
  { id: "platformlegal", label: "Legal Details", href: "/console/legal" },
  { id: "platformwalkthrough", label: "Walkthrough reports", href: "/console/walkthrough" },
  { id: "platformrestoretests", label: "Backup restore tests", href: "/console/restore-tests" },
];

/** Finance (Ledgers, Landed cost, Reports) is for Admins only; Letters of credit only when switched on (Settings → Finance → Advanced). */
const REPORT_IDS = new Set<string>(MAIN_REPORTS.map((m) => m.id));
const INV_IDS = new Set(["invstock", "invreceive", "invadjust", "invtransfer", "invmoves", "invgrnenq"]);
const FINANCE_IDS = new Set(["financeledgers", "financelc", "financelanded", "financereports", "financeexport", "logisticsdb"]);
/** Finance: a Finance row open and "Can see costs and prices" (Falcon support: always). */
/** Export to accounting: Full on Finance (Ledgers) and "Can see costs and prices", for every order (Falcon support: always). */
export const canExportToAccounting = (actor: Actor | null = state.session.actor ?? null) => !!actor?.supportAccess
  || (!!actor?.access?.costs && actorLevel(actor, "financeledgers") === "full" && actor.permissions.dataScope.type === "all");
export const isFinanceAdmin = () => !!state.session.actor?.supportAccess || financeOpen(state.session.actor?.access);
function financeVisible(id: string): boolean {
  if (!isFinanceAdmin() || !canOpen(id)) return false;
  const f = state.session.organisation?.finance;
  if (id === "logisticsdb") return !!state.session.organisation?.freightFinance || !!f?.quotesOn;
  if (id === "financelc") return !!f?.lcOn;   // an advanced feature: shown only when switched on
  if (id === "financeexport") return canExportToAccounting();
  return true;
}

/** What the organisation makes (Settings → Organisation → What you make): a switched-off industry only hides its menu
 *  items (nothing is deleted). Standards and certificates: Compliance & Certification. */
function industryShown(id: string): boolean {
  const f = state.session.organisation?.features;
  // Inventory: only when an Admin has switched it on (Settings → Inventory); Admins always have Full there.
  if (INV_IDS.has(id)) return !!state.session.organisation?.inventory;
  if (id === "compliance") return f?.standards !== false;
  return true;
}
/** The products menu is named after what the organisation makes: Garments, or Footwear when it makes only footwear. */
function navLabel(item: NavItem): string {
  // A QC inspector's own two menu items follow their screen language (everything else stays English).
  if (isInspector() && (item.id === "qcready" || item.id === "qcmine")) return t(item.id === "qcready" ? "qch.ready" : "qch.mine");
  const f = state.session.organisation?.features;
  if (item.id === "products" && f && f.garment === false) return f.footwear ? "Footwear" : "Products";
  if (item.id === "dispatch" && !state.session.actor?.supportAccess) {
    if (state.session.actor?.role === "Supplier") return "Samples to send";
    if (state.session.actor?.role === "Agent") return "Samples";
  }
  return item.label;
}

function navButton(item: NavItem, active: string, extra = ""): string {
  const label = navLabel(item);
  return `<a class="nav-btn nav-${esc(item.id)} ${extra} ${item.id === active ? "active" : ""}" href="${esc(item.href)}" data-link title="${esc(label)}">
   <span class="nav-icon">${icon(item.id)}</span>
   <span class="nav-label">${esc(label)}</span>${item.id === "tasks" ? '<span class="falcon-nav-badge" id="nav-task-badge" hidden></span>' : item.id === "help" ? '<span class="falcon-nav-badge help-badge" id="nav-help-badge" title="New in What\'s new" hidden></span>' : ""}
 </a>`;
}

/** Menu groups work as an accordion: only one is open at a time. On every page (and after a refresh) only the group
 *  holding the current page is open and highlighted; opening another group closes the one that was open. */

function navHtml(items: NavItem[], active: string, actor: Actor | null): string {
  return items.map((item) => {
    if (item.children) {
      // The order timeline is for the organisation's own team (never suppliers or agents).
      // The main Reports menu is hidden unless switched back on (the whole menu or one report).
      // Each item: the person's level on its row (No access = not in the menu).
      // Falcon CRM: every CRM screen, for everyone with Falcon CRM (the server checks what each person may do there).
      const kids = item.id === CRM_GROUP.id ? item.children : item.children.filter((c) => industryShown(c.id) && (!REPORT_IDS.has(c.id) || reportShown(state.session.reportsMenu, c.id as MainReportId))
        && (!actor || (FINANCE_IDS.has(c.id) ? financeVisible(c.id) : INV_IDS.has(c.id) ? canOpen(c.id, actor) || canOpen("settings", actor) : canOpen(c.id, actor) && (c.id !== "timeline" || actor.permissions.dataScope.type === "all"))));
      if (!kids.length) return "";
      // Only the group of the current page is open.
      const current = kids.some((k) => k.id === active);
      const open = current;
      return `<div class="nav-menu-group ${open ? "" : "is-collapsed"} ${current ? "has-active" : ""}" data-group="${esc(item.id)}"><button type="button" class="nav-menu-group-title" data-action="toggle-nav-group" aria-expanded="${open}" aria-controls="navgrp-${esc(item.id)}" title="${esc(item.label)}"><span class="nav-icon">${icon(item.id === "productsgroup" ? "products" : item.id)}</span><span class="nav-label">${esc(item.label)}</span><span class="nav-menu-chevron" aria-hidden="true">▾</span></button><div class="nav-menu-group-children" id="navgrp-${esc(item.id)}" role="group" aria-label="${esc(item.label)}">${kids.map((k) => navButton(k, active, "nav-product-child nav-group-child")).join("")}</div></div>`;
    }
    if (actor && !canOpen(item.id, actor)) return "";
    return navButton(item, active);
  }).join("");
}

onAction("toggle-nav-group", (el) => {
  const group = el.closest<HTMLElement>(".nav-menu-group")!;
  // Icons-only sidebar: the group icon shows its pop-out menu instead (tap on touch screens).
  if (iconsOnly()) { showFlyout(group); return; }
  const open = group.classList.toggle("is-collapsed") === false;
  el.setAttribute("aria-expanded", String(open));
  if (open) {
    // Accordion: opening a group closes any other open group.
    document.querySelectorAll<HTMLElement>("#nav .nav-menu-group:not(.is-collapsed)").forEach((g) => {
      if (g === group) return;
      g.classList.add("is-collapsed");
      g.querySelector(".nav-menu-group-title")?.setAttribute("aria-expanded", "false");
    });
    group.scrollIntoView({ block: "nearest" });
  }
  navFades();
});

/** A soft fade at the top / bottom of the menu when there is more to scroll that way. */
export function navFades(): void {
  const nav = document.getElementById("nav");
  if (!nav) return;
  nav.classList.toggle("nav-more-above", nav.scrollTop > 2);
  nav.classList.toggle("nav-more-below", nav.scrollTop + nav.clientHeight < nav.scrollHeight - 2);
}
document.addEventListener("scroll", (e) => { if ((e.target as HTMLElement).id === "nav") navFades(); }, true);
window.addEventListener("resize", () => navFades());

// ---- Icons-only sidebar: hovering (or tapping) a group icon shows its items in a small pop-out menu ----
const iconsOnly = () => document.body.classList.contains("sidebar-collapsed") && window.matchMedia("(min-width: 761px)").matches;
let flyoutTimer = 0;
function flyout(): HTMLElement {
  let el = document.getElementById("nav-flyout");
  if (!el) {
    el = document.createElement("div");
    el.id = "nav-flyout";
    el.className = "nav-flyout";
    el.setAttribute("role", "menu");
    el.hidden = true;
    el.addEventListener("mouseenter", () => window.clearTimeout(flyoutTimer));
    el.addEventListener("mouseleave", () => hideFlyout(150));
    el.addEventListener("click", (e) => { if ((e.target as HTMLElement).closest("a")) hideFlyout(0); });
    document.body.appendChild(el);
  }
  return el;
}
function showFlyout(group: HTMLElement): void {
  window.clearTimeout(flyoutTimer);
  const title = group.querySelector<HTMLElement>(".nav-menu-group-title");
  const kids = group.querySelectorAll<HTMLAnchorElement>(".nav-menu-group-children a.nav-btn");
  if (!title || !kids.length) return;
  const el = flyout();
  el.innerHTML = `<div class="nav-flyout-title">${esc(title.getAttribute("title") || "")}</div>${[...kids].map((a) => `<a href="${esc(a.getAttribute("href") || "")}" data-link role="menuitem" class="${a.classList.contains("active") ? "active" : ""}">${a.querySelector(".nav-icon")?.outerHTML || ""}<span>${esc(a.getAttribute("title") || "")}</span></a>`).join("")}`;
  const r = title.getBoundingClientRect();
  el.hidden = false;
  el.style.left = `${Math.round(r.right + 6)}px`;
  el.style.top = `${Math.round(Math.max(8, Math.min(r.top, window.innerHeight - el.offsetHeight - 8)))}px`;
  el.dataset.group = group.dataset.group || "";
}
function hideFlyout(delay: number): void {
  window.clearTimeout(flyoutTimer);
  const hide = () => { const el = document.getElementById("nav-flyout"); if (el) el.hidden = true; };
  if (delay <= 0) hide();   // at once (a link was chosen, Esc, a click elsewhere, or the page changed)
  else flyoutTimer = window.setTimeout(hide, delay);
}
document.addEventListener("mouseover", (e) => {
  if (!iconsOnly()) return;
  const group = (e.target as HTMLElement).closest?.<HTMLElement>(".sidebar .nav-menu-group");
  if (group) showFlyout(group);
});
document.addEventListener("mouseout", (e) => {
  const from = (e.target as HTMLElement).closest?.(".sidebar .nav-menu-group");
  const to = (e as MouseEvent).relatedTarget as HTMLElement | null;
  if (from && !to?.closest?.(".sidebar .nav-menu-group, #nav-flyout")) hideFlyout(200);
});
document.addEventListener("focusin", (e) => {
  const title = (e.target as HTMLElement).closest?.<HTMLElement>(".sidebar .nav-menu-group-title");
  if (title && iconsOnly()) showFlyout(title.closest<HTMLElement>(".nav-menu-group")!);
});
document.addEventListener("keydown", (e) => { if (e.key === "Escape") hideFlyout(0); });
document.addEventListener("click", (e) => {
  if (!(e.target as HTMLElement).closest?.(".sidebar .nav-menu-group, #nav-flyout")) hideFlyout(0);
});

let sidebarCollapsed = false;
try { sidebarCollapsed = localStorage.getItem("riman_sidebar_collapsed") === "1"; } catch { /* ignore */ }
onAction("toggle-sidebar", () => {
  sidebarCollapsed = !sidebarCollapsed;
  document.body.classList.toggle("sidebar-collapsed", sidebarCollapsed);
  hideFlyout(0);
  try { localStorage.setItem("riman_sidebar_collapsed", sidebarCollapsed ? "1" : "0"); } catch { /* ignore */ }
});

// ---- Organisation logo in the sidebar ------------------------------------------------------------------
// The logo keeps its shape and is scaled down (never up) to fit: at most the sidebar's content width and about
// 56px high, centred. It sits on a white rounded box unless the organisation ticked "Transparent background".
export const sidebarLogoHtml = (src: string, alt: string, transparent = false) =>
  `<span class="sidebar-organisation-logo-frame" data-look="${transparent ? "clear" : "box"}"><img class="sidebar-organisation-logo" src="${esc(src)}" alt="${esc(alt)}"></span>`;

export interface ShellOptions { active: string; title: string; subtitle: string; content: string }

/** Screens that keep their own layout instead of the standard screen layout (web/public/css/screens.css): the Time &
 *  Action Plan, the QC inspection steps, and printable documents. */
const OWN_LAYOUT = [/^\/app\/critical/, /^\/app\/qc\/inspect\//, /^\/app\/qc\/inspections\/[^/]+$/, /\/(document|print|preview)$/];
export const usesStandardLayout = (path: string) => path.startsWith("/app") && !OWN_LAYOUT.some((re) => re.test(path));

export function renderShell(o: ShellOptions): void {
  const s = state.session;
  const inConsole = s.mode === "platform";
  const org = s.organisation;
  const actor = s.actor ?? null;

  // The Falcon CRM portal (Falcon CRM only, or someone with both on a CRM screen): the CRM menu and name, no PLM search
  // or Action Centre. Someone with both gets the switcher on the name (and a small one when the menu is collapsed).
  if (!inConsole && org) notePortal();
  const both = !inConsole && !!org && bothPortals();
  const crmSolo = !inConsole && !!org && inCrmPortal();
  const product = crmSolo ? "FALCON CRM" : "FALCON PLM";
  const portalItem = (p: "plm" | "crm", name: string, href: string) => {
    const cur = (p === "crm") === crmSolo;
    return `<a href="${href}" data-link data-portal="${p}" class="portal-option${cur ? " cur" : ""}"${cur ? ' aria-current="true"' : ""}>${name}${cur ? '<span aria-hidden="true">✓</span>' : ""}</a>`;
  };
  const switcher = both
    ? `<button type="button" class="portal-mini" data-action="portal-menu" aria-haspopup="true" aria-controls="portal-pop" title="Switch between Falcon PLM and Falcon CRM">${crmSolo ? "CRM" : "PLM"} <span aria-hidden="true">▾</span></button>
<div class="portal-pop" id="portal-pop" role="menu" aria-label="Switch portal" hidden>${portalItem("plm", "FALCON PLM", "/app")}${portalItem("crm", "FALCON CRM", "/app/crm")}</div>`
    : "";
  const productHtml = both
    ? `<button type="button" class="portal-switch" data-action="portal-menu" aria-haspopup="true" aria-controls="portal-pop" title="Switch between Falcon PLM and Falcon CRM"><strong>${product}</strong> <span class="portal-caret" aria-hidden="true">▾</span></button>`
    : `<strong>${product}</strong>`;
  const brand = inConsole || !org
    ? `<div id="platform-brand" class="brand brand-wordmark platform-only-brand"><span class="platform-brand-copy"><strong>FALCON PLM</strong></span></div>`
    : `<div id="platform-brand" class="brand brand-wordmark organisation-workspace-brand"><span class="platform-brand-copy">${productHtml}</span><span class="platform-organisation-divider" aria-hidden="true"></span>${org.logoUrl ? sidebarLogoHtml(org.logoUrl, `${org.name} logo`, !!org.logoTransparent) : ""}<strong class="sidebar-organisation-name">${esc(org.name)}</strong></div>`;

  // Settings stays in the main menu (for those who have it); Help is in the profile menu.
  const settingsNav = inConsole
    ? navButton({ id: "settings", label: "Settings", href: "/console/settings" }, o.active === "platformsettings" ? "settings" : "", "settings-nav-btn")
    : actor && canOpen("settings", actor) ? navButton({ id: "settings", label: "Settings", href: "/app/settings" }, o.active === "settings" ? "settings" : "", "settings-nav-btn") : "";
  // "?" next to the title on every screen: the User Manual page about this screen, in a side panel.
  const profileWas = profileOpenState();
  const helpQ = !inConsole && org ? ` <button type="button" class="help-q" data-action="help-panel" title="Help for this page (or press ?)" aria-label="Help for this page">?</button>` : "";
  document.getElementById("root")!.innerHTML = `
<div class="app">
  <aside class="sidebar">
    <div class="sidebar-brand-row">
      ${brand}
      ${switcher}
      <button id="sidebar-collapse-btn" class="sidebar-collapse-btn" type="button" data-action="toggle-sidebar" title="Collapse menu" aria-label="Collapse menu">${icon("collapse")}</button>
    </div>
    <nav id="nav">${inConsole ? navHtml(CONSOLE_NAV, o.active, null) : isQcInspector(actor) ? navHtml(INSPECTOR_NAV, o.active, null) : crmSolo ? navHtml(crmMenu(!!org?.crmPlmLink), o.active, null) : navHtml(PLM_NAV, o.active, actor)}</nav>
    <div class="sidebar-footer">
      <div id="settings-nav-holder"${settingsNav ? "" : " hidden"}>${settingsNav}</div>
      ${profileHtml(inConsole, o.active)}
    </div>
  </aside>
  <main class="main">
    <header class="topbar">
      <button type="button" class="falcon-menu-btn" data-action="open-menu" aria-label="Open menu"><span></span><span></span><span></span></button>
      <div class="falcon-topbar-title">
        <h2 id="page-title">${esc(o.title)}${helpQ}</h2>
        <p id="page-subtitle">${esc(o.subtitle)}</p>
        <div id="workspace-context" class="workspace-context">${inConsole
          ? '<span class="workspace-context-badge platform">Falcon Admin Console</span>'
          : `<span class="workspace-context-badge organisation">Organisation Workspace</span><strong>${esc(org?.name || "")}</strong>`}</div>
      </div>
      <div class="top-actions top-icon-actions">
        ${!inConsole && org && !isQcInspector(actor) && !crmSolo ? `<div class="falcon-global-search" role="search"><input type="search" id="global-search" placeholder="Search codes, names, suppliers…" autocomplete="off" aria-label="Search everything" aria-controls="global-search-results"><div id="global-search-results" class="falcon-search-results" role="listbox" hidden></div></div>` : ""}
        ${!inConsole && org && !isQcInspector(actor) && !crmSolo ? `<div class="falcon-bell-wrap"><button type="button" class="falcon-bell" id="action-bell" data-action="toggle-actions" aria-haspopup="dialog" aria-expanded="false" title="Action Centre" aria-label="Action Centre">${icon("bell")}<span class="falcon-bell-count" id="action-count" hidden></span></button><div class="falcon-actions-pop" id="actions-pop" role="dialog" aria-label="Action Centre" hidden></div></div>` : ""}
        <div id="header-date-time-wrap" class="header-date-time-wrap">
          <button id="header-date-time-button" type="button" class="header-date-time-button" title="Open calendar" aria-label="Open calendar" aria-haspopup="dialog" aria-expanded="false" data-action="toggle-calendar">
            ${icon("calendar")}
            <span id="header-date-text"></span><i aria-hidden="true"></i><strong id="header-time-text"></strong>
          </button>
          <div id="header-calendar-popover" class="header-calendar-popover" role="dialog" aria-label="Calendar" hidden></div>
        </div>
        ${langButtonHtml()}
      </div>
    </header>
    <div id="falcon-announcements" class="falcon-announcements"></div>
    ${shellNoticesHtml(inConsole)}
    <section id="content">${s.supportAccess && !inConsole ? `<div class="falcon-support-banner"><strong>Falcon support access</strong><span>You opened ${esc(org?.name || "this organisation")}'s workspace. Everything you do here is recorded in its Activity Log.</span></div>` : ""}${o.content}</section>
  </main>
  <div class="falcon-menu-shade" data-action="close-menu"></div>
</div>`;
  document.body.classList.remove("falcon-menu-open");
  // The Falcon Admin Console has its own compact layout (web/public/css/console.css); it applies only on Console screens.
  document.body.classList.toggle("console-mode", inConsole);
  // Every PLM and CRM screen shares one layout for boxes, figure boxes, tables, filters and phone cards (screens.css).
  document.body.classList.toggle("std-screens", !inConsole && usesStandardLayout(location.pathname));
  document.body.classList.toggle("sidebar-collapsed", sidebarCollapsed);
  hideFlyout(0);
  document.querySelector<HTMLElement>("#nav .nav-btn.active")?.scrollIntoView({ block: "nearest" });
  navFades();
  if (!inConsole && org && !isQcInspector(actor) && !crmSolo) void refreshBadges();
  else if (!inConsole && org) void refreshHelpOnly();
  document.title = inConsole ? `FALCON PLM · Admin Console · V${s.version}` : `${product} · ${org?.name || ""} Workspace · V${s.version}`;
  updateClock();
  restoreProfile(profileWas);
  checkOrgLogos();
}

/** Notes above every page: in the Console, closed organisations ready to delete (missing legal details are a weekly
 *  email to the owner, not a banner); in a
 *  closed organisation, that it is read-only and when it will be deleted. */
function shellNoticesHtml(inConsole: boolean): string {
  const s = state.session;
  const out: string[] = [];
  if (inConsole && s.console) {
    for (const o of s.console.closingDue) out.push(`<div class="falcon-shell-notice" role="status"><strong>${esc(o.name)}</strong> <span>has been closed for 30 days and can now be deleted permanently.</span> <a href="/console/organisations/${esc(o.id)}" data-link>Review it</a></div>`);
  }
  const closed = !inConsole ? s.organisation?.closed : null;
  if (closed) {
    const when = new Date(closed.deleteAfter).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    out.push(`<div class="falcon-shell-notice is-bad" role="status" id="org-closed-banner"><strong>This organisation is closed and read-only.</strong> <span>Nothing can be changed. Everything stays viewable, and the main admin can download all the data, until ${esc(when)}; after that it will be deleted permanently.</span></div>`);
  }
  return out.length ? `<div class="falcon-shell-notices">${out.join("")}</div>` : "";
}

/** Replace just the page content (keeps the sidebar and header). */
export function setContent(html: string): void {
  const el = document.getElementById("content");
  if (!el) return;
  const banner = el.querySelector(".falcon-support-banner")?.outerHTML || "";
  el.innerHTML = banner + html;
}

// ---- Header date, time and calendar (organisation timezone) -------------------
function tz(): string | undefined {
  return state.session.organisation?.timezone || undefined;
}
function updateClock(): void {
  const d = document.getElementById("header-date-text");
  const t = document.getElementById("header-time-text");
  if (!d || !t) return;
  const now = new Date();
  try {
    d.textContent = now.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: tz() }).replace(",", "");
    t.textContent = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: tz() });
  } catch {
    d.textContent = now.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }).replace(",", "");
    t.textContent = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  }
}
setInterval(updateClock, 15_000);

let calMonth: Date | null = null;
function renderCalendar(): void {
  const pop = document.getElementById("header-calendar-popover");
  if (!pop || !calMonth) return;
  const y = calMonth.getFullYear(), m = calMonth.getMonth();
  const first = new Date(y, m, 1);
  const lead = (first.getDay() + 6) % 7; // Monday first
  const days = new Date(y, m + 1, 0).getDate();
  const [ty, tm, td] = orgToday().split("-").map(Number);
  const cells: string[] = [];
  for (let i = 0; i < lead; i++) cells.push("<span></span>");
  for (let d = 1; d <= days; d++) {
    const isToday = d === td && m === tm - 1 && y === ty;
    cells.push(`<span class="${isToday ? "today" : ""}">${d}</span>`);
  }
  pop.innerHTML = `<div class="falcon-cal-head"><button type="button" class="icon-btn" data-action="cal-prev" aria-label="Previous month">‹</button><strong>${first.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</strong><button type="button" class="icon-btn" data-action="cal-next" aria-label="Next month">›</button></div>
<div class="falcon-cal-grid">${["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((x) => `<b>${x}</b>`).join("")}${cells.join("")}</div>`;
}
export function closeCalendar(): void {
  const pop = document.getElementById("header-calendar-popover");
  if (pop && !pop.hidden) {
    pop.hidden = true;
    document.getElementById("header-date-time-button")?.setAttribute("aria-expanded", "false");
  }
}
onAction("toggle-calendar", (el) => {
  const pop = document.getElementById("header-calendar-popover")!;
  if (!pop.hidden) return closeCalendar();
  const [cy, cm] = orgToday().split("-").map(Number);
  calMonth = new Date(cy, cm - 1, 1);
  renderCalendar();
  pop.hidden = false;
  el.setAttribute("aria-expanded", "true");
});
onAction("cal-prev", () => { if (calMonth) { calMonth = new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1); renderCalendar(); } });
onAction("cal-next", () => { if (calMonth) { calMonth = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1); renderCalendar(); } });

// ---- Common building blocks ---------------------------------------------------
export function backBar(href: string, label: string): string {
  return `<div class="console-return-bar"><a class="secondary falcon-back-btn" href="${esc(href)}" data-link>← ${esc(label)}</a></div>`;
}

export function loadingPanel(): string {
  return `<div class="panel"><div class="empty">Loading…</div></div>`;
}

export function errorPanel(message: string): string {
  // Falcon busy for a moment (it already tried again once): say so, with a way to try again.
  const busy = /^Falcon is busy/.test(message);
  return `<div class="panel${busy ? " load-failed" : ""}"${busy ? ' role="alert"' : ""}><div class="empty">${esc(message)}${busy ? ` <a href="${esc(location.pathname + location.search)}" data-link>Try again</a>` : ""}</div></div>`;
}

// ---- Phone and tablet menu ------------------------------------------------------
onAction("open-menu", () => document.body.classList.add("falcon-menu-open"));
onAction("close-menu", () => document.body.classList.remove("falcon-menu-open"));

// ---- Portal switcher (Falcon PLM and Falcon CRM) ----------------------------------
// With the menu collapsed the list floats beside it (the narrow menu would cut it off).
function closePortalMenu(): void {
  const p = document.getElementById("portal-pop");
  if (p && !p.hidden) { p.hidden = true; document.querySelectorAll("[data-action=portal-menu]").forEach((b) => b.setAttribute("aria-expanded", "false")); }
}
onAction("portal-menu", () => {
  const p = document.getElementById("portal-pop");
  if (!p) return;
  if (!p.hidden) return closePortalMenu();
  const mini = document.querySelector<HTMLElement>(".portal-mini");
  const floating = document.body.classList.contains("sidebar-collapsed") && !!mini && mini.offsetParent !== null;
  p.classList.toggle("portal-pop-float", floating);
  if (floating) { const r = mini!.getBoundingClientRect(); p.style.left = `${Math.round(r.right + 8)}px`; p.style.top = `${Math.round(r.top)}px`; }
  else { p.style.left = ""; p.style.top = ""; }
  p.hidden = false;
  document.querySelectorAll("[data-action=portal-menu]").forEach((b) => b.setAttribute("aria-expanded", "true"));
});
// Choosing a portal remembers it before the screen changes (so /app opens Falcon PLM, not the last portal).
document.addEventListener("click", (e) => {
  const t = e.target as HTMLElement;
  const a = t.closest<HTMLElement>("[data-portal]");
  if (a) { rememberPortal(a.dataset.portal === "crm" ? "crm" : "plm"); closePortalMenu(); return; }
  if (!t.closest("#portal-pop, [data-action=portal-menu]")) closePortalMenu();
}, true);
document.addEventListener("keydown", (e) => { if (e.key === "Escape") closePortalMenu(); });

// ---- Header search (codes, names, suppliers, agents) ----------------------------------
let searchTimer: number | undefined;
let searchSeq = 0;
async function runSearch(q: string): Promise<void> {
  const box = document.getElementById("global-search-results");
  if (!box) return;
  if (q.trim().length < 2) { box.hidden = true; return; }
  const seq = ++searchSeq;
  try {
    const { results } = await get<{ results: Array<{ kind: string; title: string; subtitle: string; href: string }> }>(`/api/org/search?q=${encodeURIComponent(q.trim())}`);
    if (seq !== searchSeq) return;
    box.innerHTML = results.length
      ? results.map((r, i) => `<a href="${esc(r.href)}" data-link class="falcon-search-hit ${i === 0 ? "active" : ""}" role="option"><small>${esc(r.kind)}</small><strong>${esc(r.title)}</strong><span>${esc(r.subtitle)}</span></a>`).join("")
      : `<div class="falcon-search-empty">Nothing found for "${esc(q.trim())}".</div>`;
    box.hidden = false;
  } catch {
    box.hidden = true;
  }
}
export function closeSearch(): void {
  const box = document.getElementById("global-search-results");
  if (box) box.hidden = true;
}
document.addEventListener("input", (e) => {
  const t = e.target as HTMLInputElement;
  if (t.id !== "global-search") return;
  window.clearTimeout(searchTimer);
  searchTimer = window.setTimeout(() => runSearch(t.value), 250);
});
document.addEventListener("keydown", (e) => {
  const t = e.target as HTMLInputElement;
  if (t.id !== "global-search") return;
  const hits = Array.from(document.querySelectorAll<HTMLAnchorElement>("#global-search-results .falcon-search-hit"));
  const at = hits.findIndex((h) => h.classList.contains("active"));
  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
    e.preventDefault();
    if (!hits.length) return;
    const next = (at + (e.key === "ArrowDown" ? 1 : -1) + hits.length) % hits.length;
    hits.forEach((h, i) => h.classList.toggle("active", i === next));
  } else if (e.key === "Enter" && hits.length) {
    e.preventDefault();
    hits[Math.max(at, 0)].click();
  } else if (e.key === "Escape") {
    closeSearch();
  }
});

// ---- Action Centre bell and My Tasks badge ----------------------------------------------------
let badgeAt = 0, badges: { openTasks: number; actions: number; notifications?: number } | null = null;
let helpStatus: { unread: number; announcements: Array<{ id: string; kind: string; message: string; endsAt: string | null }> } | null = null;
const dismissedKey = () => `falcon_dismissed_announcements_${state.session.user?.id || "anon"}`;
function dismissed(): string[] { try { return JSON.parse(localStorage.getItem(dismissedKey()) || "[]"); } catch { return []; } }
/** Announcement banners from Falcon (maintenance and urgent stay; info can be dismissed) and the What's new badge. */
function renderHelpStatus(): void {
  const b = document.getElementById("nav-help-badge"), box = document.getElementById("falcon-announcements");
  if (b && helpStatus) { b.hidden = !helpStatus.unread; b.textContent = helpStatus.unread > 9 ? "9+" : String(helpStatus.unread); }
  if (box && helpStatus) {
    const gone = new Set(dismissed());
    box.innerHTML = helpStatus.announcements.filter((a) => a.kind !== "info" || !gone.has(a.id)).map((a) => `<div class="falcon-announcement falcon-announcement-${esc(a.kind)}" role="${a.kind === "info" ? "status" : "alert"}"><strong>${a.kind === "maintenance" ? "Planned maintenance" : a.kind === "urgent" ? "Important" : "Falcon news"}</strong><span>${esc(a.message)}</span>${a.kind === "info" ? `<button type="button" class="link-btn" data-action="dismiss-announcement" data-id="${esc(a.id)}" aria-label="Dismiss">Dismiss</button>` : ""}</div>`).join("");
  }
}
onAction("dismiss-announcement", (el) => {
  try { localStorage.setItem(dismissedKey(), JSON.stringify([...dismissed(), el.dataset.id].slice(-100))); } catch { /* private window */ }
  renderHelpStatus();
});
/** A QC inspector has no Action Centre: only the help status (What's new badge, announcements). */
async function refreshHelpOnly(): Promise<void> {
  try { helpStatus = await get("/api/help/status"); } catch { return; }
  renderHelpStatus();
}
export async function refreshBadges(force = false): Promise<void> {
  if (force || !badges || Date.now() - badgeAt > 60_000) {
    try {
      [badges, helpStatus] = await Promise.all([get("/api/org/badges"), get("/api/help/status").catch(() => helpStatus)]);
      badgeAt = Date.now();
    } catch { return; }
  }
  renderHelpStatus();
  const t = document.getElementById("nav-task-badge"), c = document.getElementById("action-count");
  if (t && badges) { t.hidden = !badges.openTasks; t.textContent = String(badges.openTasks); }
  if (c && badges) { const n = badges.actions + (badges.notifications || 0); c.hidden = !n; c.textContent = n > 99 ? "99+" : String(n); c.classList.toggle("has-new", !!badges.notifications); }
}
// Opening a notification marks it read.
document.addEventListener("click", (e) => {
  const a = (e.target as HTMLElement).closest<HTMLElement>("[data-note]");
  if (!a) return;
  void post("/api/org/notifications/read", { ids: [a.dataset.note] }).then(() => refreshBadges(true)).catch(() => {});
  closeActions();
}, true);
onAction("notes-read-all", async () => {
  try { await post("/api/org/notifications/read", { all: true }); document.querySelectorAll(".falcon-note.unread").forEach((x) => x.classList.remove("unread")); await refreshBadges(true); } catch (err) { toast((err as Error).message, "error"); }
});
export function closeActions(): void {
  const pop = document.getElementById("actions-pop");
  if (pop && !pop.hidden) { pop.hidden = true; document.getElementById("action-bell")?.setAttribute("aria-expanded", "false"); }
}
const PRIORITY_LABEL: Record<string, string> = { high: "High", due: "Due", normal: "Action" };
export function actionListHtml(list: Array<{ priority: string; area: string; action: string; subject: string; detail: string; due: string; href: string }>, empty = "Nothing needs your action right now."): string {
  return list.length ? `<div class="falcon-action-list">${list.map((x) => `<a class="falcon-action-row ${esc(x.priority)}" href="${esc(x.href)}" data-link><span class="falcon-action-pri">${esc(PRIORITY_LABEL[x.priority] || "Action")}</span><span class="falcon-action-main"><strong>${esc(x.action)}</strong><small>${esc(x.subject)}${x.detail ? ` · ${esc(x.detail)}` : ""}</small></span><span class="falcon-action-area">${esc(x.area)}${x.due ? `<small>${esc(x.due.split("-").reverse().join("/"))}</small>` : ""}</span></a>`).join("")}</div>` : `<div class="empty">${esc(empty)}</div>`;
}
onAction("toggle-actions", async (el) => {
  const pop = document.getElementById("actions-pop")!;
  if (!pop.hidden) return closeActions();
  pop.hidden = false;
  el.setAttribute("aria-expanded", "true");
  pop.innerHTML = `<div class="falcon-actions-head"><strong>Action Centre</strong><span class="muted">Work waiting for you. It disappears once done.</span></div><div class="empty">Loading…</div>`;
  try {
    const [{ actions }, notes] = await Promise.all([get<{ actions: any[] }>("/api/org/actions"), get<any>("/api/org/notifications?limit=15").catch(() => ({ unread: 0, notifications: [] }))]);
    const recent = (notes.notifications as any[]).filter((n) => !n.read || Date.now() - Date.parse(n.at) < 7 * 86400_000).slice(0, 8);
    pop.innerHTML = `${recent.length ? `<div class="falcon-actions-head"><strong>Notifications</strong><span class="muted">${notes.unread} unread</span>${notes.unread ? '<button type="button" class="link-btn" data-action="notes-read-all">Mark all read</button>' : ""}</div>
<div class="falcon-note-list">${recent.map((n) => `<a class="falcon-note ${n.read ? "" : "unread"}" href="${esc(n.href || "#")}" data-link data-note="${esc(n.id)}"><strong>${esc(n.title)}</strong><small>${esc(n.body.slice(0, 160))}${n.body.length > 160 ? "…" : ""}</small><small class="muted">${esc(new Date(n.at).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }))}</small></a>`).join("")}</div>` : ""}
<div class="falcon-actions-head"><strong>Action Centre</strong><span class="muted">${actions.length} item${actions.length === 1 ? "" : "s"} waiting. Each disappears once done.</span></div>${actionListHtml(actions)}`;
    if (badges) { badges.actions = actions.length; badges.notifications = notes.unread; void refreshBadges(); }
  } catch (err) {
    pop.innerHTML = `<div class="empty">${esc((err as Error).message)}</div>`;
  }
});
